/**
 * A message may not be scheduled into the past.
 *
 * WHAT WAS WRONG
 * The compose dialog warned about a past send time and then let Send through
 * anyway. Someone who read the warning, agreed with it, and pressed Send got a
 * message dispatched immediately — or, worse, a campaign-side `expired` — with
 * the product having said in advance that this was a problem and then done it
 * regardless. A warning that the product itself ignores teaches people to ignore
 * warnings.
 *
 * WHAT THIS IS
 * ONE predicate, returning BOTH the warning and the block, so the two can never
 * disagree. The previous arrangement had the warning computed in
 * `schedule-summary.tsx` and no block computed anywhere; the failure mode of
 * re-deriving "is it past?" in the footer would have been a dialog that warns and
 * does not block, or blocks and does not say why — which is where we started.
 *
 * THE GRACE IS DELIBERATE and is carried over unchanged from the expression this
 * grew out of: a due time under a minute old is NOT past. Someone who set 14:30
 * at 14:30 must be able to press Send.
 *
 * RECURRING MESSAGES ARE JUDGED THE SAME WAY, ON THEIR dueDate. This module
 * used to exempt every recurring draft that carried a rule, on the premise that
 * a series' dueDate is an anchor that stays behind while the series carries on.
 * That premise is false, and it let a recurring message be scheduled to START in
 * the past with Send enabled (four live reproductions, 2026-09-11). The backend
 * gives every fired occurrence a SUCCESSOR document whose `dueDate` is the next
 * send (`buildNextOccurrenceDoc`, whatsapp-scheduler-backend
 * src/jobs/next-occurrence.ts), so the dueDate of a pending recurring message IS
 * its first send. It is exempt only while that first send is still ahead — which
 * is exactly the non-recurring rule, grace included. `isRecurring` / `rruleset`
 * stay on the input so the call sites keep describing the draft in full, but
 * they no longer change the verdict.
 *
 * Where a recurring start has passed, the remedy is the recurrence popover: its
 * Done moves the series to the first occurrence that has not
 * (`resolveRecurrenceCommit`) and says so before committing. This gate only
 * refuses to send what is still in the past, and says why.
 */

import { SCHEDULE_PAST_GRACE_MS } from '@common/scheduling/past-grace';

export interface PastTimeInput {
    /** The draft's due date, as stored — an ISO string, a Date, or nothing yet. */
    dueDate: unknown;
    /** The draft repeats. Does NOT change the verdict — see the header. */
    isRecurring?: boolean;
    /** The serialized rule. Does NOT change the verdict — see the header. */
    rruleset?: string | null;
    /** Injected so this is testable and so both callers can share one clock. */
    now?: number;
}

export interface PastTimeState {
    /** Show the "that time has passed" line. */
    warn: boolean;
    /** Refuse to schedule, and say so where the reason is already shown. */
    block: boolean;
}

const NOT_PAST: PastTimeState = { warn: false, block: false };

/** A due time under this old still counts as "now". Shared with the recurrence popover. */
export const PAST_TIME_GRACE_MS = SCHEDULE_PAST_GRACE_MS;

export const resolvePastTimeState = (input: PastTimeInput): PastTimeState => {
    const { dueDate } = input;
    const now = input.now ?? Date.now();

    if (dueDate === null || dueDate === undefined || dueDate === '') return NOT_PAST;

    // `new Date` on an ISO string, a Date, or a number — all three occur on this
    // draft depending on which path last touched it. Anything it cannot read is
    // NOT treated as past: an unparseable date is a different complaint, it is
    // already refused by the dialog's own date validation, and shouting "that time
    // has passed" at someone who has typed nothing legible is just noise.
    const at = dueDate instanceof Date ? dueDate.getTime() : new Date(dueDate as string).getTime();
    if (!Number.isFinite(at)) return NOT_PAST;

    const past = at < now - PAST_TIME_GRACE_MS;
    // One decision, two consumers. If these ever need to diverge, that is a
    // product decision to make HERE, in one place, not by one caller quietly
    // computing its own.
    return past ? { warn: true, block: true } : NOT_PAST;
};
